export enum RequirementType {
  ORIENTATION = 'ORIENTATION',
  INTERVIEW = 'INTERVIEW',
  TOUR = 'TOUR',
  TRAINING = 'TRAINING',
  DRUG_TEST = 'DRUG_TEST',
  ADDED_TO_EXTERNAL_VMS = 'ADDED_TO_EXTERNAL_VMS',
  ADDED_TO_BUSINESS_SCHEDULE = 'ADDED_TO_BUSINESS_SCHEDULE',
  SALIVA_DRUG_TEST = 'SALIVA_DRUG_TEST',
  PPE_VERIFICATION = 'PPE_VERIFICATION',
  MEDICAL_EXAMINATION = 'MEDICAL_EXAMINATION',
  URGENT_CARE_DRUG_TEST = 'URGENT_CARE_DRUG_TEST',
  RESUME_SENT_TO_BIZ = 'RESUME_SENT_TO_BIZ',
  VIRTUAL_TRAINING_SESSION = 'VIRTUAL_TRAINING_SESSION',
  ASYNC_VIDEO = 'ASYNC_VIDEO',
  ONBOARDING_QUIZ = 'ONBOARDING_QUIZ',
  ONBOARDING_FORM = 'ONBOARDING_FORM',
  IN_PERSON_EVALUATION = 'IN_PERSON_EVALUATION',
  PROFILE_PICTURE_VALIDATION = 'PROFILE_PICTURE_VALIDATION',
  DOCUMENT_COLLECTION = 'DOCUMENT_COLLECTION',
  WORKER_INFORMATION_VERIFICATION = 'WORKER_INFORMATION_VERIFICATION',
}

/**
 * Requirement types that should appear AFTER the W2 Onboarding step in the
 * worker pipeline. These are external-system bookkeeping steps that only make
 * sense once onboarding is complete.
 */
export const POST_ONBOARDING_REQUIREMENT_TYPES: ReadonlySet<RequirementType> =
  new Set([
    RequirementType.ADDED_TO_EXTERNAL_VMS,
    RequirementType.ADDED_TO_BUSINESS_SCHEDULE,
    RequirementType.DOCUMENT_COLLECTION,
  ])

export const DRUG_TEST_REQUIREMENT_TYPES: ReadonlySet<RequirementType> =
  new Set([
    RequirementType.DRUG_TEST,
    RequirementType.SALIVA_DRUG_TEST,
    RequirementType.URGENT_CARE_DRUG_TEST,
  ])

export enum RequirementStatus {
  ACTIVE = 'ACTIVE',
  ARCHIVED = 'ARCHIVED',
  STALE = 'STALE',
}

export enum RequirementShiftType {
  INDIVIDUAL = 'INDIVIDUAL',
  GROUP = 'GROUP',
}

export enum ApprovalType {
  NOT_REQUIRED = 'NOT_REQUIRED',
  OPS = 'OPS',
  BUSINESS = 'BUSINESS',
  SYSTEM = 'SYSTEM',
  ONSITE = 'ONSITE',
}

export enum OnRequirementExpiration {
  PERSIST_ACTIVE = 'PERSIST_ACTIVE',
  PERSIST_ALL = 'PERSIST_ALL',
  INVALIDATE = 'INVALIDATE',
}

export enum RequirementVisibility {
  WORKER = 'WORKER',
  BUSINESS = 'BUSINESS',
  ONSITE = 'ONSITE',
}

export interface RequirementMetadata {
  linkedOrientationIds?: string[]
  /** @deprecated Use `linkedOrientationIds` (array). Kept for read-side backward compat. */
  linkedOrientationId?: string
  interview_duration?: number
  [key: string]: unknown
}

export interface RequirementResponse {
  id: string
  companyId: string
  name: string
  requirementType: RequirementType
  status: RequirementStatus
  shiftType: RequirementShiftType
  approvalType: ApprovalType
  onRequirementExpiration: OnRequirementExpiration
  /**
   * Max age (in days) of a completed worker requirement before it expires.
   * Server DTO emits this as `maxAgeDays`. Optional — only set on
   * requirement types that support expiration (e.g. drug tests).
   */
  maxAgeDays?: number
  visibility: RequirementVisibility[]
  locationIds?: string[]
  roleIds?: string[]
  /** Location IDs associated with this requirement (matches server DTO field name) */
  associatedLocationIds?: string[]
  /** Role IDs associated with this requirement (matches server DTO field name) */
  associatedRoleIds?: string[]
  slotsRequested: number
  blocksNextStep: boolean
  createdAt: string
  updatedAt: string
  metadata?: RequirementMetadata
  workerRequirements?: WorkerRequirementRecord[]
}

export interface WorkerRequirementRecord {
  id: string
  createdAt: string
  updatedAt: string
  workerId: string
  requirementId: string
  status: string
  completedAt?: string
  approvedBy?: string
  waived: boolean
  waivedReason?: string
  reversalReason?: string
  workerShiftId?: string
  shiftId?: string
  workerName: string
  metadata?: {
    linkedDrugTestId?: string
    drugTestCreatedAt?: string
    drugTestExpiresAt?: string
    memo?: string
    [key: string]: unknown
  }
}

/** A step within a requirement Pipeline (the first-class entity). */
export interface PipelineRequirementResponse {
  requirementId: string
  sortOrder: number
  dependsOnRequirementId?: string
  requirementOverrides?: Record<string, unknown>
}

/** Lifecycle status of a requirement Pipeline. */
export enum PipelineStatus {
  ACTIVE = 'ACTIVE',
  ARCHIVED = 'ARCHIVED',
}

/** The first-class requirement Pipeline entity (replaces the derived flow). */
export interface PipelineResponse {
  id: string
  createdAt: string
  updatedAt: string
  name: string
  description?: string
  status: PipelineStatus
  companyId: string
  requirements: PipelineRequirementResponse[]
}

export interface CreatePipelineRequest {
  companyId: string
  name: string
  description?: string
  requirements: PipelineRequirementResponse[]
}

/** Fully-shaped pipeline card for the ops Library: an ordered, resolved set of requirements plus the schedules it is attached to. */
export interface PipelineCardResponse {
  id: string
  name: string
  status: PipelineStatus
  companyId: string
  requirements: RequirementResponse[]
  shiftRequestParentIds: string[]
  dependsOnByRequirementId?: Record<string, string>
}

export interface AttachPipelineRequest {
  shiftRequestId: string
  pipelineId: string
}

export interface ArchivePipelineRequest {
  /**
   * Must be true to archive a pipeline that still constrains shift requests
   * with upcoming shifts; archiving then detaches those shift requests.
   */
  acknowledgeActiveShiftRequests?: boolean
}

export interface PipelineArchiveImpactRequirement {
  id: string
  name: string
}

/** Preview of what archiving a pipeline will do — drives the ops confirm/warning modal. */
export interface PipelineArchiveImpactResponse {
  pipelineId: string
  pipelineName: string
  /**
   * True when the pipeline still constrains shift requests with upcoming
   * shifts; archiving then requires acknowledgement and detaches them.
   */
  requiresAcknowledgement: boolean
  activeShiftRequestCount: number
  activeScheduleTitles: string[]
  /** Requirements archived with the pipeline (no other active pipeline uses them). */
  requirementsToArchive: PipelineArchiveImpactRequirement[]
  /** Requirements kept active because another active pipeline uses them. */
  requirementsToKeep: PipelineArchiveImpactRequirement[]
}

export interface CreateRequirementRequest {
  companyId: string
  name: string
  requirementType: RequirementType
  shiftType?: RequirementShiftType
  approvalType?: ApprovalType
  onExpiration?: OnRequirementExpiration
  locationIds: string[]
  roleIds: string[]
  slotsRequested: number
  visibility?: RequirementVisibility[]
  blocksNextStep: boolean
  metadata?: RequirementMetadata
}

/**
 * Explicit dedup-resolution intent set by the wizard after the user
 * resolves a match in Step 2 (Review settings). When set, the backend
 * trusts the client's intent and short-circuits the implicit dedup path.
 *
 * - `use-existing`: link to `intentMatchedRequirementId` as-is (no mutation)
 * - `update-shared`: PATCH the matched requirement with the entry's
 *   settings, then link to it (changes ripple to every flow using it)
 * - `create-new`: force-insert a fresh requirement (= legacy `forceNew: true`)
 */
export type IntentResolution = 'use-existing' | 'update-shared' | 'create-new'

export interface BulkCreateRequirementEntry {
  tempKey: string
  requirementType: RequirementType
  name: string
  companyId: string
  approvalType: ApprovalType
  description?: string
  shiftType?: RequirementShiftType
  maxAllowedSlotsOnShift?: number
  dependsOnShiftRequestId?: string
  // `null` explicitly clears the expiration window on an update-shared resolve
  // (the backend only clears the column for an explicit null, not undefined).
  maxAgeDays?: number | null
  onRequirementExpiration?: OnRequirementExpiration
  slotsRequested?: number
  visibility?: RequirementVisibility[]
  locationIds?: string[]
  roleIds?: string[]
  metadata?: RequirementMetadata
  /** Legacy: skip dedup; force-insert. Kept for back-compat; superseded by intentResolution=create-new. */
  forceNew?: boolean
  /** Explicit wizard resolution; takes precedence over `forceNew`. */
  intentResolution?: IntentResolution
  /** Required when `intentResolution` is `use-existing` or `update-shared`. Verified server-side. */
  intentMatchedRequirementId?: string
}

export interface BulkCreateRequirementsRequest {
  entries: BulkCreateRequirementEntry[]
}

export interface BulkCreateRequirementsResponse {
  requirements: RequirementResponse[]
  tempKeyToId: Record<string, string>
  /** tempKeys of entries that matched an existing requirement via dedup. */
  existingTempKeys: string[]
}

export interface UpdateRequirementAssociationsRequest {
  requirementId: string
  roleIds?: string[]
  locationIds?: string[]
}

/** Body for `PATCH /requirements/:id`. Backend accepts only these editable fields. */
export interface UpdateRequirementRequest {
  requirementId: string
  name?: string
  approvalType?: ApprovalType
  visibility?: RequirementVisibility[]
  metadata?: RequirementMetadata
  /**
   * Expiration window (in days) for a completed drug test. Only valid on
   * drug-test requirement types. Pass `null` to clear (no expiration).
   */
  maxAgeDays?: number | null
  onRequirementExpiration?: OnRequirementExpiration
}

/**
 * Bulk pre-commit dedupe match. Caller sends N (type, scope) tuples;
 * server returns the existing requirement that would dedupe against each
 * one (if any), ripple counts so callers can see what mutating the shared
 * requirement would affect, and any granting shifts already published
 * against it so the UI can offer "use as-is" vs "fork" vs "update shared"
 * resolutions. Currently consumed by the prereq-flow wizard but the
 * contract is intentionally generic so other UIs can reuse it.
 */
export interface BulkMatchEntry {
  tempKey: string
  requirementType: RequirementType
  roleIds: string[]
  locationIds: string[]
}

export interface BulkMatchRequest {
  companyId: string
  entries: BulkMatchEntry[]
  /**
   * Mirrors BulkCreateRequirementsBody. When set, entries are run through
   * the same shift-context scope normalization as bulk-create before
   * deduping so preview-match agrees with what commit will silently merge.
   */
  shiftRoleId?: string
  shiftLocationId?: string
}

export interface BulkMatchGrantingShiftSummary {
  shiftRequestId: string
  shiftRequestParentId?: string
  title?: string
  roleId: string
  locationId: string
  slotsRequested: number
  payRateCents: number
}

/**
 * Per-match requirement payload returned by `POST /requirements/query-bulk-match`.
 * Mirrors `RequirementResponseDto` exactly (field-for-field) rather than the
 * legacy `RequirementResponse` shape, so consumers like the wizard read the
 * fields the server actually sends — notably `onRequirementExpiration`
 * (not `onExpiration`). Some fields on `RequirementResponse`
 * (`slotsRequested`, `blocksNextStep`, `shiftType`, `onExpiration`) are
 * frontend-only inventions that the DTO does not emit, and including them
 * here would let consumers silently read `undefined` as a valid value.
 */
export interface BulkMatchRequirement {
  id: string
  createdAt: string
  updatedAt: string
  requirementType: RequirementType
  name: string
  description?: string
  status: RequirementStatus
  companyId: string
  shiftType?: RequirementShiftType
  maxAllowedSlotsOnShift?: number
  dependsOnShiftRequestId?: string
  approvalType: ApprovalType
  maxAgeDays?: number
  onRequirementExpiration?: OnRequirementExpiration
  visibility: RequirementVisibility[]
  metadata?: RequirementMetadata
  associatedLocationIds: string[]
  associatedRoleIds: string[]
}

export interface BulkMatch {
  requirement: BulkMatchRequirement
  /** Distinct shift-request parents (≈ flows) constrained by this requirement. */
  usedByFlowCount: number
  /** Distinct shift requests constrained by this requirement. */
  usedByShiftRequestCount: number
  existingGrantingShifts: BulkMatchGrantingShiftSummary[]
}

export interface BulkMatchResult {
  tempKey: string
  match: BulkMatch | null
}

export interface BulkMatchResponse {
  results: BulkMatchResult[]
}

export interface FindMatchingRequirementsQuery {
  companyId: string
  requirementType?: RequirementType
  roleId?: string
  locationId?: string
}

export interface RequirementConstraintResponse {
  id: string
  requirementId: string
  shiftRequestId: string
  requirement: RequirementResponse
}

export interface RequirementGrantResponse {
  id: string
  requirementId: string
  shiftRequestId: string
  requirement: RequirementResponse
}

export interface RequirementTypeConfig {
  label: string
  isAsync: boolean
  hasSchedule: boolean
  hasPayRate: boolean
  hasGrantingShift: boolean
}

export const REQUIREMENT_TYPE_CONFIG: Record<
  RequirementType,
  RequirementTypeConfig
> = {
  [RequirementType.ORIENTATION]: {
    label: 'Orientation',
    isAsync: false,
    hasSchedule: true,
    hasPayRate: true,
    hasGrantingShift: true,
  },
  [RequirementType.INTERVIEW]: {
    label: 'Interview',
    isAsync: false,
    hasSchedule: true,
    hasPayRate: true,
    hasGrantingShift: true,
  },
  [RequirementType.TOUR]: {
    label: 'Tour',
    isAsync: false,
    hasSchedule: true,
    hasPayRate: true,
    hasGrantingShift: true,
  },
  [RequirementType.TRAINING]: {
    label: 'Training',
    isAsync: false,
    hasSchedule: true,
    hasPayRate: true,
    hasGrantingShift: true,
  },
  [RequirementType.DRUG_TEST]: {
    label: 'Drug Test',
    isAsync: true,
    hasSchedule: false,
    hasPayRate: false,
    hasGrantingShift: false,
  },
  [RequirementType.ADDED_TO_EXTERNAL_VMS]: {
    label: 'Added to External VMS',
    isAsync: true,
    hasSchedule: false,
    hasPayRate: false,
    hasGrantingShift: false,
  },
  [RequirementType.ADDED_TO_BUSINESS_SCHEDULE]: {
    label: 'Added to Business Schedule',
    isAsync: true,
    hasSchedule: false,
    hasPayRate: false,
    hasGrantingShift: false,
  },
  [RequirementType.SALIVA_DRUG_TEST]: {
    label: 'Saliva Drug Test',
    isAsync: true,
    hasSchedule: false,
    hasPayRate: false,
    hasGrantingShift: false,
  },
  [RequirementType.PPE_VERIFICATION]: {
    label: 'PPE Verification',
    isAsync: true,
    hasSchedule: false,
    hasPayRate: false,
    hasGrantingShift: false,
  },
  [RequirementType.MEDICAL_EXAMINATION]: {
    label: 'Medical Examination',
    isAsync: true,
    hasSchedule: false,
    hasPayRate: false,
    hasGrantingShift: false,
  },
  [RequirementType.URGENT_CARE_DRUG_TEST]: {
    label: 'Urgent Care Drug Test',
    isAsync: true,
    hasSchedule: false,
    hasPayRate: false,
    hasGrantingShift: false,
  },
  [RequirementType.RESUME_SENT_TO_BIZ]: {
    label: 'Resume Sent to Biz',
    isAsync: true,
    hasSchedule: false,
    hasPayRate: false,
    hasGrantingShift: false,
  },
  [RequirementType.VIRTUAL_TRAINING_SESSION]: {
    label: 'Virtual Training Session',
    isAsync: true,
    hasSchedule: false,
    hasPayRate: false,
    hasGrantingShift: false,
  },
  [RequirementType.ASYNC_VIDEO]: {
    label: 'Async Video',
    isAsync: true,
    hasSchedule: false,
    hasPayRate: false,
    hasGrantingShift: false,
  },
  [RequirementType.ONBOARDING_QUIZ]: {
    label: 'Onboarding Quiz',
    isAsync: true,
    hasSchedule: false,
    hasPayRate: false,
    hasGrantingShift: false,
  },
  [RequirementType.ONBOARDING_FORM]: {
    label: 'Onboarding Form',
    isAsync: true,
    hasSchedule: false,
    hasPayRate: false,
    hasGrantingShift: false,
  },
  [RequirementType.IN_PERSON_EVALUATION]: {
    label: 'In Person Evaluation',
    isAsync: true,
    hasSchedule: false,
    hasPayRate: false,
    hasGrantingShift: false,
  },
  [RequirementType.PROFILE_PICTURE_VALIDATION]: {
    label: 'Profile Picture Validation',
    isAsync: true,
    hasSchedule: false,
    hasPayRate: false,
    hasGrantingShift: false,
  },
  [RequirementType.DOCUMENT_COLLECTION]: {
    label: 'In-Person Document Collection',
    isAsync: false,
    hasSchedule: true,
    hasPayRate: true,
    hasGrantingShift: true,
  },
  [RequirementType.WORKER_INFORMATION_VERIFICATION]: {
    label: 'Worker Information Verification',
    isAsync: true,
    hasSchedule: false,
    hasPayRate: false,
    hasGrantingShift: false,
  },
}

export const REQUIREMENT_TYPE_OPTIONS = Object.entries(
  REQUIREMENT_TYPE_CONFIG,
).map(([value, config]) => ({
  value: value as RequirementType,
  label: config.label,
}))

export const REQUIREMENT_TYPE_LABELS: Record<string, string> =
  Object.fromEntries(
    Object.entries(REQUIREMENT_TYPE_CONFIG).map(([key, config]) => [
      key,
      config.label,
    ]),
  )

/** Schedule returned from the granting shift details API (GET /requirements/granting-shifts) */
export interface GrantingShiftScheduleResponse {
  startTime: string
  endTime: string
  isRecurringSchedule: boolean
  timeZone: string
  recurringEndDate?: string
  recurringFrequency?: string
  recurringInterval?: number
  recurringDays: string[]
}

/** Response item from GET /requirements/granting-shifts */
export interface GrantingShiftDetailResponse {
  requirementId: string
  shiftRequestId: string
  payRate: number
  payType?: string
  hourlyRate?: number
  schedule?: GrantingShiftScheduleResponse
}

export interface PrerequisiteFormItem {
  /** Client-side ID for stable React keys on new (not-yet-saved) items */
  clientId?: string
  // Existing requirement (selected from modal)
  existingRequirementId?: string
  existingRequirement?: RequirementResponse
  isExisting: boolean

  // Requirement fields
  requirementType?: RequirementType
  name: string
  locationIds: string[]
  roleIds: string[]
  slotsRequested: number
  approvalType?: ApprovalType
  visibility: RequirementVisibility[]
  blockedByPrevious: boolean

  // Granting shift fields
  payRate?: number
  incentiveAmount?: number
  /** Optional override location for the granting shift (instead of inferring from scoped locations) */
  grantingShiftLocationId?: string
  /** Optional override role for the granting shift (instead of inferring from scoped roles) */
  grantingShiftRoleId?: string
  /** Optional override company for the granting shift (e.g. when the shift happens at another company's premises). */
  grantingShiftCompanyId?: string
  /** Optional override supervisor from the granting shift company. */
  grantingShiftSupervisorId?: string

  /**
   * Overbooking for the granting shift. Mirror the regular shift-request
   * fields. Default (both undefined) = no overbooking. `overbookSlotsRequested`
   * is the total target including overbook (>= slotsRequested). Dynamic
   * requires >= MIN_SLOTS_REQUESTED_REQUIRED_FOR_DYNAMIC_OB slots.
   */
  overbookSlotsRequested?: number
  enableDynamicOverbooking?: boolean

  // Fetched granting shift details (populated on import)
  grantingShiftDetails?: GrantingShiftDetailResponse

  // Granting shift schedules for new requirements
  schedules: PrerequisiteScheduleItem[]

  linkedOrientationIds?: string[]

  /**
   * Expiration window (in days) for a completed drug test. Only meaningful /
   * editable for drug-test requirement types. Empty = no expiration.
   */
  maxAgeDays?: number
  onRequirementExpiration?: OnRequirementExpiration
}

/** Individual schedule within a requirement (for both display and creation) */
export interface PrerequisiteScheduleItem {
  /** Client-side ID for UI tracking */
  clientId: string
  isRecurringSchedule: boolean
  scheduleName?: string
  startDate?: Date
  endDate?: Date
  startTime?: Date
  endTime?: Date
  repeatFrequency?: 'WEEKLY' | 'BIWEEKLY'
  repeatOn?: string[]
  /** Week B days for biweekly schedules */
  repeatOnWeekB?: string[]
  /** Whether this is a duplicate created from editing an existing schedule */
  isDuplicate?: boolean
  /** Status for fetched schedules (ACTIVE, CANCELED, etc.) */
  status?: string
}
